import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import DoctorSidebar from "./DoctorSidebar";
import "./PatientRecords.css";

const PatientRecords = () => {
  const { patientId } = useParams();
  const { doctorUser, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  useEffect(() => {
    const fetchPatientRecords = async () => {
      setIsLoading(true);
      setError(null);
      if (!doctorUser || doctorUser.role !== "doctor") {
        setError("You must be logged in as a doctor to view patient records.");
        setTimeout(() => navigate('/doctor-login'), 2000);
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/appointments/patient/${patientId}/records`, {
          headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
        });

        console.log('Patient Records Response:', response.data);

        setAppointments(Array.isArray(response.data.appointments) ? response.data.appointments : []);
        setPrescriptions(Array.isArray(response.data.prescriptions) ? response.data.prescriptions : []);
      } catch (error) {
        console.error("Error fetching patient records:", error.response?.data || error.message);
        const errorMsg = error.response?.data?.message || error.response?.data?.error || error.message;
        setError("Failed to load patient records: " + errorMsg);
        if (error.response?.status === 401) {
          console.log("401 detected - Logging out and redirecting to login");
          logout('doctor');
          setTimeout(() => navigate("/doctor-login"), 2000);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatientRecords();
  }, [patientId, doctorUser, logout, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDownload = async (fileData) => {
    if (!fileData.path) {
      setError("No file path provided for download.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      if (fileData.type === "prescription" && fileData.prescriptionId) {
        // Use the direct download endpoint for prescriptions
        const response = await api.get(`/appointments/prescriptions/${fileData.prescriptionId}/download-direct-doctor`, {
          headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
          responseType: 'blob', // Handle binary data
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileData.filename || `prescription_${fileData.prescriptionId}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Handle appointment records (non-prescription files)
        let downloadUrl = fileData.path;
        if (fileData.path.includes('f005.backblazeb2.com')) {
          const fileName = fileData.path.split('/file/turemedicine-files/')[1];
          downloadUrl = `https://turemedicine-files.s3.us-east-005.backblazeb2.com/${fileName}`;
        } else {
          const normalizedPath = fileData.path.includes('/') ? fileData.path.split('/').pop() : fileData.path;
          downloadUrl = `http://127.0.0.1:5000/uploads/${normalizedPath}`;
        }
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      setError("Failed to download file: " + (error.response?.data?.message || error.message));
      setTimeout(() => setError(null), 3000);
      console.error("Download error:", error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  if (isLoading) {
    return (
      <div className="patient-records-container">
        <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="records-content">
          <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading patient records...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="patient-records-container">
        <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="records-content">
          <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <section className="welcome-section">
            <h1>Error</h1>
            <p className="error-message">{error}</p>
            <button onClick={() => { setError(null); navigate("/doctor-login"); }} aria-label="Go to login">
              Go to Login
            </button>
            <button onClick={() => { setError(null); navigate("/doctor-dashboard"); }} aria-label="Back to dashboard">
              Back to Dashboard
            </button>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="patient-records-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="records-content">
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <section className="welcome-section">
          <h1>Patient Records</h1>
          {error && <div className="error-message">{error}</div>}
        </section>

        <section className="patient-records-card">
          <h2>Previous Appointments</h2>
          {appointments.length > 0 ? (
            <div className="appointments-list">
              {appointments.map((app) => (
                <div key={app._id} className="appointment-card">
                  <p><strong>Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  <p><strong>Symptoms:</strong> {app.symptoms || "N/A"}</p>
                  <p><strong>Status:</strong> {app.status || "Pending"}</p>
                  {app.notes && <p><strong>Notes:</strong> {app.notes}</p>}
                  {app.records && app.records.length > 0 && (
                    <>
                      <h3>Records:</h3>
                      <ul>
                        {app.records.map((record, index) => (
                          <li key={index}>
                            {record.filename} -
                            <button 
                              className="action-btn" 
                              onClick={() => handleDownload({ path: record.path, type: "record", filename: record.filename })}
                              aria-label={`Download ${record.filename}`}
                            >
                              Download
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No previous appointments found.</p>
          )}
        </section>

        <section className="patient-records-card">
          <h2>Prescriptions</h2>
          {prescriptions.length > 0 ? (
            <div className="prescriptions-list">
              {prescriptions.map((pres, index) => (
                <div key={index} className="prescription-card">
                  <p>
                    <strong>Date Issued:</strong>{' '}
                    {pres.createdAt ? new Date(pres.createdAt).toLocaleString() : 'Date not available'}
                  </p>
                  <p>
                    <strong>Issued By:</strong> Dr. {pres.doctorName || 'Unknown Doctor'}
                  </p>
                  <h3>Medications:</h3>
                  <ul>
                    {pres.medications && pres.medications.length > 0 ? (
                      pres.medications.map((med, medIndex) => (
                        <li key={medIndex}>
                          {med.name} - {med.dosage} ({med.frequency}, {med.duration})
                        </li>
                      ))
                    ) : (
                      <li>No medications listed.</li>
                    )}
                  </ul>
                  <p><strong>Instructions:</strong> {pres.instructions || "N/A"}</p>
                  {pres.pdfPath && (
                    <button 
                      className="action-btn" 
                      onClick={() => handleDownload({ 
                        path: pres.pdfPath, 
                        type: "prescription", 
                        prescriptionId: pres._id, 
                        filename: `Prescription-${new Date(pres.createdAt).toLocaleDateString().replace(/\//g, '-')}.pdf`
                      })} 
                      aria-label="Download prescription PDF"
                    >
                      Download PDF
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No prescriptions found.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default PatientRecords;